import React from 'react';
import { View, TouchableOpacity } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { Text } from '@/components/ui/text';
import { useTranslation } from '../../hooks/useTranslation';

interface TierRenewal {
  name: string;
  fullPriceFormatted: string;
}

interface FinalOfferBannerProps {
  /** True once the user accepted — renders a compact confirmation instead. */
  armed: boolean;
  /** Fixed first-month price, already formatted in the user's checkout currency (e.g. "$10", "€10", "₺499"). */
  fixedPriceLabel: string;
  /** The two monthly tiers this offer applies to (Students, Chasing Credentials) — never Mentor Coach packs. */
  tiers: TierRenewal[];
  onAccept: () => void;
  onDismiss: () => void;
}

/**
 * Paywall-abandonment "final offer" — step 2 of the ladder, shown only after
 * the comeback offer (step 1) was shown and dismissed/declined. Clearly
 * framed as the last step: fixed low first-month price, paid now, no trial,
 * monthly tiers only. Mandatory renewal-transparency copy is rendered
 * directly on the card (once per applicable tier) and is repeated at the
 * actual checkout CTA on each plan card — see PlanSelectionScreenEnhanced.
 */
export default function FinalOfferBanner({ armed, fixedPriceLabel, tiers, onAccept, onDismiss }: FinalOfferBannerProps) {
  const { t } = useTranslation();

  if (armed) {
    return (
      <View
        className="rounded-2xl px-4 py-3 mb-6 flex-row items-center"
        style={{
          backgroundColor: '#FDF2F8',
          borderWidth: 1,
          borderColor: '#FBCFE8',
          gap: 10,
        }}
      >
        <MaterialIcons name="check-circle" size={18} color="#9D174D" />
        <Text className="text-sm font-semibold flex-1" style={{ color: '#831843' }}>
          {t('billing.finalOffer.appliedBadge', { price: fixedPriceLabel })}
        </Text>
      </View>
    );
  }

  return (
    <View
      className="rounded-2xl px-5 py-4 mb-6"
      style={{
        backgroundColor: '#FDF2F8',
        borderWidth: 1,
        borderColor: '#F9A8D4',
        gap: 12,
      }}
      accessibilityRole="none"
      testID="final-offer-banner"
    >
      <View className="flex-row items-start" style={{ gap: 12 }}>
        <View
          style={{
            width: 40,
            height: 40,
            borderRadius: 20,
            backgroundColor: '#FCE7F3',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <MaterialIcons name="local-fire-department" size={20} color="#9D174D" />
        </View>
        <View style={{ flex: 1 }}>
          <Text className="text-base font-extrabold" style={{ color: '#831843' }}>
            {t('billing.finalOffer.title')}
          </Text>
          <Text className="text-sm mt-1" style={{ color: '#9D174D' }}>
            {t('billing.finalOffer.subtitle', { price: fixedPriceLabel })}
          </Text>
        </View>
        <TouchableOpacity
          onPress={onDismiss}
          accessibilityRole="button"
          accessibilityLabel={t('billing.finalOffer.dismissA11y')}
          testID="final-offer-dismiss"
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        >
          <MaterialIcons name="close" size={18} color="#9D174D" />
        </TouchableOpacity>
      </View>

      {/* Mandatory renewal-transparency copy, once per applicable tier. */}
      <View style={{ gap: 4 }}>
        {tiers.map((tier) => (
          <Text key={tier.name} className="text-xs" style={{ color: '#BE185D' }}>
            {tier.name}: {t('billing.finalOffer.renewalNotice', { price: tier.fullPriceFormatted })}
          </Text>
        ))}
      </View>

      <TouchableOpacity
        onPress={onAccept}
        accessibilityRole="button"
        testID="final-offer-accept"
        style={{
          backgroundColor: '#9D174D',
          borderRadius: 10,
          paddingVertical: 10,
          alignItems: 'center',
        }}
      >
        <Text className="text-sm font-bold" style={{ color: '#FFFFFF' }}>
          {t('billing.finalOffer.cta', { price: fixedPriceLabel })}
        </Text>
      </TouchableOpacity>
    </View>
  );
}
